import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Bot, Zap, BarChart3, Users, CheckCircle2, ArrowRight, Sparkles, Brain, MessageSquare, TrendingUp, Shield, Globe, Crown, Gem, HelpCircle, ChevronDown } from 'lucide-react'
import '@/lib/i18n'
import Container from '@/components/ui/Container'
import Section from '@/components/ui/Section'
import Button from '@/components/ui/Button'
import PricingCard from '@/components/ui/PricingCard'
import PromoCard from '@/components/ui/PromoCard'
import { useSeo, PRODUCT_URL } from '@/lib/seo'

export default function BassiliChatAIPage() {
  const { t } = useTranslation(['translation', 'home', 'bassilichatai'])
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  useSeo({
    title: 'BassiliChat AI — Compare 7 AI Models Side by Side | Pricing from $0',
    description:
      'BassiliChat AI compares ChatGPT, Claude, Gemini, Grok, DeepSeek, Perplexity and Mistral in one prompt, then merges them into one answer. Free plan, plus Pro ($9), Elite ($19) and Platinum ($29) per month.',
    path: '/bassilichatai',
  })

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Auto-rotate screenshots
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % 6)
    }, 3000) // Change image every 3 seconds

    return () => clearInterval(interval)
  }, [])

  const features = [
    {
      icon: <BarChart3 className="w-6 h-6" />,
      titleKey: 'bassilichatai:features.compare.title',
      descriptionKey: 'bassilichatai:features.compare.description',
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      titleKey: 'bassilichatai:features.analysis.title',
      descriptionKey: 'bassilichatai:features.analysis.description',
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      titleKey: 'bassilichatai:features.interactive.title',
      descriptionKey: 'bassilichatai:features.interactive.description',
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      titleKey: 'bassilichatai:features.performance.title',
      descriptionKey: 'bassilichatai:features.performance.description',
    },
    {
      icon: <Brain className="w-6 h-6" />,
      titleKey: 'bassilichatai:features.educational.title',
      descriptionKey: 'bassilichatai:features.educational.description',
    },
    {
      icon: <Shield className="w-6 h-6" />,
      titleKey: 'bassilichatai:features.secure.title',
      descriptionKey: 'bassilichatai:features.secure.description',
    },
  ]

  const benefits = [
    {
      icon: <Zap className="w-5 h-5" />,
      textKey: 'bassilichatai:benefits.saveTime',
    },
    {
      icon: <CheckCircle2 className="w-5 h-5" />,
      textKey: 'bassilichatai:benefits.informed',
    },
    {
      icon: <Users className="w-5 h-5" />,
      textKey: 'bassilichatai:benefits.perfect',
    },
    {
      icon: <Globe className="w-5 h-5" />,
      textKey: 'bassilichatai:benefits.accessible',
    },
  ]

  return (
    <div className="relative pt-20">
      {/* Hero Section - Unique Browser Mockup Design */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-slate-950 dark:via-blue-950 dark:to-indigo-950 py-20">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-20 dark:opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(to right, #0072ff 1px, transparent 1px), linear-gradient(to bottom, #0072ff 1px, transparent 1px)`,
              backgroundSize: '4rem 4rem',
            }}
          />
        </div>

        {/* Floating Gradient Orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute top-20 left-10 w-72 h-72 rounded-full bg-gradient-to-br from-blue-400/30 to-cyan-400/30 dark:from-blue-500/20 dark:to-cyan-500/20 blur-3xl"
            animate={{
              scale: [1, 1.3, 1],
              x: [0, 30, 0],
              y: [0, 20, 0],
            }}
            transition={{
              duration: 18,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
          <motion.div
            className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-gradient-to-br from-purple-400/30 to-pink-400/30 dark:from-purple-500/20 dark:to-pink-500/20 blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              x: [0, -40, 0],
              y: [0, -25, 0],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        </div>

        <Container className="relative z-10">
          <div className="max-w-7xl mx-auto">
            {/* Top Content Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16 max-w-4xl mx-auto"
            >
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-blue-500/10 to-purple-500/10 dark:from-blue-500/20 dark:to-purple-500/20 border border-blue-500/20 dark:border-blue-500/30 mb-8 backdrop-blur-sm"
              >
                <Sparkles className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                  Compare 7 AI models — free plan, no card required
                </span>
              </motion.div>

              {/* Main Heading */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="space-y-6 mb-8"
              >
                <h1 className="text-6xl sm:text-7xl lg:text-8xl font-black leading-[1.05]">
                  <span className="text-gray-900 dark:text-white">1 Click.</span>
                  <br />
                  <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 dark:from-blue-400 dark:via-purple-400 dark:to-indigo-400 bg-clip-text text-transparent">
                    7 Answers.
                  </span>
                </h1>
                <p className="text-xl md:text-2xl lg:text-3xl text-gray-700 dark:text-gray-300 leading-relaxed max-w-3xl mx-auto">
                  Compare <span className="font-bold text-gray-900 dark:text-white">ChatGPT, Claude, Gemini</span>, and more — side-by-side.
                  <br />
                  Get a unified, <span className="font-semibold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent">calculated summary</span> and copy key parts to a built-in Notepad.
                </p>
              </motion.div>

              {/* Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8"
              >
                <motion.a
                  href={PRODUCT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600 text-white rounded-2xl font-bold text-lg shadow-xl shadow-blue-500/30 hover:shadow-2xl hover:shadow-blue-500/50 transition-all duration-300"
                >
                  Try it Free
                  <ArrowRight className="w-6 h-6" />
                </motion.a>
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-2xl font-bold text-lg border-2 border-gray-300 dark:border-slate-600 hover:border-blue-500 dark:hover:border-blue-500 transition-all duration-300 shadow-lg"
                  onClick={() => {
                    const element = document.querySelector('#features')
                    element?.scrollIntoView({ behavior: 'smooth' })
                  }}
                >
                  How it Works
                </motion.button>
              </motion.div>

              {/* Features List */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.7 }}
                className="flex flex-wrap gap-8 justify-center"
              >
                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                  <CheckCircle2 className="w-6 h-6 text-green-500" />
                  <span className="font-semibold text-lg">Privacy-first</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                  <CheckCircle2 className="w-6 h-6 text-green-500" />
                  <span className="font-semibold text-lg">Save $$$ vs. multiple subs</span>
                </div>
              </motion.div>
            </motion.div>

            {/* Browser Mockup with Screenshots */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="relative max-w-6xl mx-auto"
            >
              {/* Main Browser Window */}
              <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-slate-700">
                {/* Browser Header */}
                <div className="flex items-center gap-2 px-4 py-3 bg-gray-100 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                  </div>
                  <div className="flex-1 mx-4">
                    <div className="bg-white dark:bg-slate-700 rounded-lg px-4 py-1.5 text-sm text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-slate-600">
                      bassilichat.ai
                    </div>
                  </div>
                </div>

                {/* Screenshot Display Area - No Cropping */}
                <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800 p-4">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentImageIndex}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.5 }}
                      className="relative bg-white dark:bg-slate-900 rounded-lg shadow-lg overflow-hidden"
                    >
                      <img
                        src={`/images/${currentImageIndex + 1}.png`}
                        alt={`BassiliChat Screenshot ${currentImageIndex + 1}`}
                        className="w-full h-auto object-contain"
                        style={{ maxHeight: '500px' }}
                      />
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>

              {/* Floating Screenshot Thumbnails */}
              <div className="hidden lg:block">
                {/* Left Floating Thumbnails */}
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                  className="absolute -left-24 top-20 space-y-4"
                >
                  {[0, 1].map((index) => (
                    <motion.button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      whileHover={{ scale: 1.1, x: 5 }}
                      className={`block w-20 h-20 rounded-xl overflow-hidden shadow-lg border-2 transition-all ${
                        currentImageIndex === index
                          ? 'border-blue-500 ring-4 ring-blue-500/30'
                          : 'border-gray-300 dark:border-slate-600 hover:border-blue-400'
                      }`}
                    >
                      <img
                        src={`/images/${index + 1}.png`}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-contain bg-white dark:bg-slate-800"
                      />
                    </motion.button>
                  ))}
                </motion.div>

                {/* Right Floating Thumbnails */}
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                  className="absolute -right-24 top-20 space-y-4"
                >
                  {[2, 3].map((index) => (
                    <motion.button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      whileHover={{ scale: 1.1, x: -5 }}
                      className={`block w-20 h-20 rounded-xl overflow-hidden shadow-lg border-2 transition-all ${
                        currentImageIndex === index
                          ? 'border-blue-500 ring-4 ring-blue-500/30'
                          : 'border-gray-300 dark:border-slate-600 hover:border-blue-400'
                      }`}
                    >
                      <img
                        src={`/images/${index + 1}.png`}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-contain bg-white dark:bg-slate-800"
                      />
                    </motion.button>
                  ))}
                </motion.div>

                {/* Bottom Floating Thumbnails */}
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1 }}
                  className="absolute -bottom-12 left-1/2 -translate-x-1/2 flex gap-4"
                >
                  {[4, 5].map((index) => (
                    <motion.button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      whileHover={{ scale: 1.1, y: -5 }}
                      className={`block w-20 h-20 rounded-xl overflow-hidden shadow-lg border-2 transition-all ${
                        currentImageIndex === index
                          ? 'border-blue-500 ring-4 ring-blue-500/30'
                          : 'border-gray-300 dark:border-slate-600 hover:border-blue-400'
                      }`}
                    >
                      <img
                        src={`/images/${index + 1}.png`}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-contain bg-white dark:bg-slate-800"
                      />
                    </motion.button>
                  ))}
                </motion.div>
              </div>

              {/* Mobile Indicators */}
              <div className="lg:hidden mt-8 flex gap-3 justify-center">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`rounded-full transition-all duration-300 ${
                      currentImageIndex === index
                        ? 'bg-blue-600 w-8 h-3'
                        : 'bg-gray-300 dark:bg-gray-600 w-3 h-3 hover:bg-blue-400'
                    }`}
                    aria-label={`View screenshot ${index + 1}`}
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Features Section */}
      <Section id="features" className="bg-white dark:bg-slate-900">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
              {t('bassilichatai:features.title')}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {t('bassilichatai:features.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-gray-50 to-white dark:from-slate-800 dark:to-slate-900 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-slate-700 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 text-white mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">
                  {t(feature.titleKey)}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  {t(feature.descriptionKey)}
                </p>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Benefits Section */}
      <Section className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-slate-800 dark:via-blue-950 dark:to-indigo-950">
        <Container>
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
                {t('bassilichatai:benefits.title')}
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {t('bassilichatai:benefits.subtitle')}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-4">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-center gap-4 bg-white dark:bg-slate-900 rounded-lg p-4 shadow-md border border-gray-100 dark:border-slate-700"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 text-white flex items-center justify-center">
                    {benefit.icon}
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 font-medium">
                    {t(benefit.textKey)}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* Use Cases Section */}
      <Section className="bg-white dark:bg-slate-900">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
              {t('bassilichatai:useCases.title')}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {t('bassilichatai:useCases.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                titleKey: 'bassilichatai:useCases.developers.title',
                descriptionKey: 'bassilichatai:useCases.developers.description',
                gradient: 'from-blue-500 to-cyan-500',
              },
              {
                titleKey: 'bassilichatai:useCases.researchers.title',
                descriptionKey: 'bassilichatai:useCases.researchers.description',
                gradient: 'from-purple-500 to-pink-500',
              },
              {
                titleKey: 'bassilichatai:useCases.enthusiasts.title',
                descriptionKey: 'bassilichatai:useCases.enthusiasts.description',
                gradient: 'from-orange-500 to-red-500',
              },
            ].map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-gray-50 to-white dark:from-slate-800 dark:to-slate-900 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-slate-700 hover:shadow-xl transition-all duration-300"
              >
                <div className={`w-16 h-16 rounded-xl bg-gradient-to-r ${useCase.gradient} mb-4 flex items-center justify-center text-white text-2xl font-bold`}>
                  {t(useCase.titleKey)[0]}
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">
                  {t(useCase.titleKey)}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  {t(useCase.descriptionKey)}
                </p>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Promo Section - Get 7 LLMs */}
      <Section className="bg-white dark:bg-slate-900 py-12">
        <Container>
          <PromoCard
            title={t('bassilichatai:promo.title')}
            subtitle={t('bassilichatai:promo.subtitle')}
            description={t('bassilichatai:promo.description')}
            buttonText={t('bassilichatai:promo.button')}
            buttonLink={PRODUCT_URL}
            icon={<Sparkles className="w-6 h-6" />}
          />
        </Container>
      </Section>

      {/* Pricing Section */}
      <Section className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-slate-950 dark:via-blue-950 dark:to-indigo-950 relative overflow-hidden py-12 min-h-[85vh] flex items-center">
        <Container className="relative z-10 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-2 text-gray-900 dark:text-white">
              {t('bassilichatai:pricing.title')}
            </h2>
            <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {t('bassilichatai:pricing.subtitle')}
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {/* Free Plan */}
            <PricingCard
              name={t('bassilichatai:pricing.starter.name')}
              icon={<Zap className="w-6 h-6" />}
              price={0}
              period=""
              description={t('bassilichatai:pricing.starter.description')}
              features={(t('bassilichatai:pricing.starter.features', { returnObjects: true }) as string[]).map((text: string) => ({ text, included: true }))}
              hiddenFeatures={(t('bassilichatai:pricing.starter.hiddenFeatures', { returnObjects: true }) as string[]).map((text: string) => ({ text, included: false }))}
              buttonText={t('bassilichatai:pricing.starter.button')}
              buttonLink={PRODUCT_URL}
              gradient="from-blue-500 to-cyan-500"
              showMoreText={t('bassilichatai:pricing.showMore')}
              showLessText={t('bassilichatai:pricing.showLess')}
              delay={0}
            />

            {/* Pro Plan */}
            <PricingCard
              name={t('bassilichatai:pricing.pro.name')}
              icon={<Bot className="w-6 h-6" />}
              price={9}
              description={t('bassilichatai:pricing.pro.description')}
              features={(t('bassilichatai:pricing.pro.features', { returnObjects: true }) as string[]).map((text: string) => ({ text, included: true }))}
              hiddenFeatures={(t('bassilichatai:pricing.pro.hiddenFeatures', { returnObjects: true }) as string[]).map((text: string) => ({ text, included: false }))}
              buttonText={t('bassilichatai:pricing.pro.button')}
              buttonLink={PRODUCT_URL}
              gradient="from-sky-500 to-blue-500"
              showMoreText={t('bassilichatai:pricing.showMore')}
              showLessText={t('bassilichatai:pricing.showLess')}
              delay={0.1}
            />

            {/* Elite Plan — Most Popular */}
            <PricingCard
              name={t('bassilichatai:pricing.elite.name')}
              icon={<Crown className="w-6 h-6" />}
              price={19}
              description={t('bassilichatai:pricing.elite.description')}
              features={(t('bassilichatai:pricing.elite.features', { returnObjects: true }) as string[]).map((text: string) => ({ text, included: true }))}
              hiddenFeatures={(t('bassilichatai:pricing.elite.hiddenFeatures', { returnObjects: true }) as string[]).map((text: string) => ({ text, included: false }))}
              buttonText={t('bassilichatai:pricing.elite.button')}
              buttonLink={PRODUCT_URL}
              isPopular={true}
              gradient="from-purple-500 to-pink-500"
              showMoreText={t('bassilichatai:pricing.showMore')}
              showLessText={t('bassilichatai:pricing.showLess')}
              delay={0.2}
            />

            {/* Platinum Plan */}
            <PricingCard
              name={t('bassilichatai:pricing.platinum.name')}
              icon={<Gem className="w-6 h-6" />}
              price={29}
              description={t('bassilichatai:pricing.platinum.description')}
              features={(t('bassilichatai:pricing.platinum.features', { returnObjects: true }) as string[]).map((text: string) => ({ text, included: true }))}
              hiddenFeatures={(t('bassilichatai:pricing.platinum.hiddenFeatures', { returnObjects: true }) as string[]).map((text: string) => ({ text, included: false }))}
              buttonText={t('bassilichatai:pricing.platinum.button')}
              buttonLink={PRODUCT_URL}
              gradient="from-indigo-500 to-slate-700"
              showMoreText={t('bassilichatai:pricing.showMore')}
              showLessText={t('bassilichatai:pricing.showLess')}
              delay={0.3}
            />
          </div>
        </Container>
      </Section>

      {/* FAQ Section */}
      <Section className="bg-white dark:bg-slate-900">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
              {t('bassilichatai:faq.title')}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {t('bassilichatai:faq.subtitle')}
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {(t('bassilichatai:faq.items', { returnObjects: true }) as { q: string; a: string }[]).map((item, index) => (
              <motion.details
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="group bg-gradient-to-br from-gray-50 to-white dark:from-slate-800 dark:to-slate-900 rounded-xl border border-gray-100 dark:border-slate-700 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
              >
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none p-5 font-semibold text-gray-900 dark:text-white select-none">
                  <span className="flex items-start gap-3">
                    <HelpCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    {item.q}
                  </span>
                  <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 group-open:rotate-180" />
                </summary>
                <div className="px-5 pb-5 pl-[3.25rem] text-gray-600 dark:text-gray-400 leading-relaxed">
                  {item.a}
                </div>
              </motion.details>
            ))}
          </div>
        </Container>
      </Section>

      {/* CTA Section */}
      <Section className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 dark:from-blue-700 dark:via-indigo-700 dark:to-purple-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '4rem 4rem',
            }}
          />
        </div>

        <Container className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              {t('bassilichatai:cta.title')}
            </h2>
            <p className="text-xl text-white/90 mb-8">
              {t('bassilichatai:cta.subtitle')}
            </p>
            <a
              href={PRODUCT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold text-lg hover:shadow-2xl transition-all duration-300 hover:scale-105"
            >
              {t('bassilichatai:cta.tryNow')}
              <ArrowRight className="w-5 h-5" />
            </a>
          </motion.div>
        </Container>
      </Section>
    </div>
  )
}

